import { FUEL, KERB_LANE } from '@accra/shared';
import type { EventBus } from '../../core/events';
import type { RunState } from '../../game/RunState';
import { STATION_HALF, nextStation } from '../../world/fuelStations';
import { GRADE, type DrivingAssist } from '../driving';
import { isSettledIn } from '../lanes';
import { isActive } from '../pickups/powerUps';
import { stopAssist } from '../passengers/stopAssist';

/** Seconds standing still with a dead engine before the shift is over. */
const STALL_SECONDS = 3.5;
/** Under this many units short of full, the pump doesn't bother you. */
const TOP_UP_MARGIN = 3;

const OFF: DrivingAssist = { maxSpeed: Number.POSITIVE_INFINITY };

function inBay(state: RunState): boolean {
  const station = nextStation(state.bus.s);
  if (!station) return false;
  const { bus } = state;
  return Math.abs(bus.s - station.position) <= STATION_HALF - 1 && bus.speed < 1.5 && isSettledIn(bus.x, KERB_LANE, 1.2);
}

/** Burns petrol for the distance driven: more with the gas pedal and the siren; a trickle when standing still. */
function burn(state: RunState, metres: number, gas: boolean, dt: number): void {
  const { fuel, bus } = state;
  if (fuel.units <= 0) return;
  let rate = Math.max(GRADE.minBurn, 1 + bus.grade * GRADE.burn) / FUEL.metresPerUnit;
  if (gas) rate *= FUEL.gasFactor;
  if (isActive(state, 'siren')) rate *= FUEL.sirenFactor;
  const idle = bus.speed < 0.5 ? FUEL.idleUnitsPerSecond * dt : 0;
  fuel.units = Math.max(0, fuel.units - metres * rate - idle);
}

function warn(state: RunState, events: EventBus): void {
  const { fuel, bus } = state;
  const [first, second, third] = FUEL.warnAt;
  const level = fuel.units <= 0 ? 4 : fuel.units <= third ? 3 : fuel.units <= second ? 2 : fuel.units <= first ? 1 : 0;
  if (level === 0) fuel.warned = 0;
  if (level === 0 || level <= fuel.warned) return;
  fuel.warned = level;
  if (level === 4) {
    bus.engineOff = true;
    events.emit('fuel:empty', {});
    return;
  }
  const station = nextStation(bus.s);
  events.emit('fuel:low', { level, units: Math.round(fuel.units), station: station?.name ?? null, distance: station ? Math.round(station.position - bus.s) : 0 });
}

/** The pump: fills while you stand in the bay, charging for every unit. */
function pump(state: RunState, dt: number, events: EventBus): void {
  const { fuel, stats } = state;
  const station = nextStation(state.bus.s);
  const wants = station !== null && inBay(state) && fuel.units < FUEL.tankUnits - 1e-6;
  if (!wants) {
    fuel.filling = false;
    return;
  }
  if (!fuel.filling) events.emit('fuel:filling', { station: station.name });
  fuel.filling = true;
  const added = Math.min(FUEL.fillUnitsPerSecond * dt, FUEL.tankUnits - fuel.units);
  fuel.units += added;
  fuel.bought += added;
  // Whole units are what the server prices; rounding up means we never under-charge.
  stats.fuelUnits = Math.ceil(fuel.bought - 1e-9);
  stats.fuelPesewas = stats.fuelUnits * FUEL.pesewasPerUnit;
  state.bus.engineOff = false;
  if (fuel.units >= FUEL.tankUnits - 1e-6) {
    fuel.filling = false;
    events.emit('fuel:full', { station: station.name });
  }
}

/** A dead engine that has stopped rolling ends the shift. */
function stall(state: RunState, dt: number, events: EventBus): void {
  const { bus, fuel } = state;
  if (!bus.engineOff || bus.speed > 0.5) {
    fuel.stalledFor = 0;
    return;
  }
  fuel.stalledFor += dt;
  if (fuel.stalledFor < STALL_SECONDS) return;
  state.over = true;
  state.endReason = 'breakdown';
  state.crashCause = 'fuel';
  events.emit('run:brokeDown', { legName: 'out of fuel' });
}

/** Every tick: burn fuel for the distance driven, warn, run the pump, and end the shift if the engine stays dead. */
export function updateFuel(state: RunState, metres: number, gas: boolean, dt: number, events: EventBus): void {
  burn(state, metres, gas, dt);
  warn(state, events);
  pump(state, dt, events);
  stall(state, dt, events);
}

/** Glides you into the pump bay, and holds the trotro there while it fills (gas overrides). Off when the tank is nearly full. */
export function fuelAssist(state: RunState, gas: boolean): DrivingAssist {
  const station = nextStation(state.bus.s);
  if (!station) return OFF;
  // Once the pump is running, the bus stays put until the tank is full.
  if (state.fuel.filling) return stopAssist(state.bus, station, gas, 'holding');
  if (state.fuel.units >= FUEL.tankUnits - TOP_UP_MARGIN) return OFF;
  return stopAssist(state.bus, station, gas, 'approach');
}
